import React, { useState, useEffect } from 'react';
import * as XLSX from 'xlsx';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import { Upload, Download, Loader2, ChevronRight, FileSpreadsheet } from 'lucide-react';
import { 
  KPIDataProcessor, 
  IH_CONFIG, 
  PH_CONFIG, 
  ProcessedItem,
  formatCellValue,
  ProcessorResult 
} from './kpiDataProcessor';
import { toast } from '@/components/ui/toast';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { FileType } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { ScrollArea } from "@/components/ui/scroll-area";

type SplitType = "Program" | "Provider" | "ParentProvider";

interface SheetData {
  [key: string]: any[];
}

interface ProcessedData {
  workbook: XLSX.WorkBook;
  allSheetData: SheetData;
  summarySheet?: string;
}

interface WorkOrderData {
  workOrder: string;
  summary: any[][];
  kpi1: any[][];
  kpi2: any[][];
  kpi3: any[][];
}

interface ProcessedWorkOrder {
  workOrder: string;
  displayName: string; // First 3 digits
  fullName: string; // Full work order
  data: WorkOrderData;
}

const fileTypes = {
  "Program": "IH KPI (by Work Order)",
  "Provider": "PH KPI (by Provider)", 
  "ParentProvider": "Parent Provider Report"
};

const icmsTabsToInclude = ["Summary", "KPI-1-6mo", "KPI-1-1yr", "KPI-2", "KPI-3"];

const getParentOrganization = (programName: string): string => {
  return "Parent Organization";
};

const setAllPrograms = (programs: string[]): void => {
  console.log("Programs stored:", programs);
};


// **CRITICAL FIX 1: Column Insertion Function**
const insertSplitColumn = (
  originalRows: any[][],
  splitValue: string,
  splitType: SplitType
): any[][] => {
  const columnHeader = "ClientID";
  
  return originalRows.map((row, index) => {
    if (!row) return [];
    
    const newRow = [...row];
    
    if (index <= 2) {
      // Header rows - insert column title or empty
      newRow.unshift(index === 2 ? columnHeader : "");
    } else {
      // Data rows - insert the split value
      newRow.unshift(splitValue);
    }
    
    return newRow;
  });
};

// **CRITICAL FIX 2: Precise Data Filtering**
const filterRowsForValue = (
  originalRows: any[][],
  splitValue: string,
  isInSummarySheet: boolean
): any[][] => {
  console.log(`\n=== Filtering for: "${splitValue}" ===`);
  console.log(`Sheet type: ${isInSummarySheet ? 'Summary' : 'Regular'}, Total rows: ${originalRows.length}`);
  
  const result = originalRows.filter((row, index) => {
    // Always keep header rows
    const headerRowCount = isInSummarySheet ? 3 : 2;
    if (index < headerRowCount) {
      console.log(`Row ${index}: KEEPING header row`);
      return true;
    }
    
    // For data rows, check all columns for program match
    if (!row || row.length === 0) {
      console.log(`Row ${index}: SKIPPING empty row`);
      return false;
    }
    
    // For data rows, check if any cell contains the program name
    let foundMatch = false;
    for (let colIndex = 0; colIndex < row.length; colIndex++) {
      const cellValue = row[colIndex];
      if (cellValue && typeof cellValue === 'string') {
        const trimmedValue = cellValue.trim();
        // Check for exact match or if the split value is contained in the cell
        // Also apply ODR-IHS and total filtering
        if ((trimmedValue === splitValue || trimmedValue.includes(splitValue)) &&
            trimmedValue.includes('ODR-IHS') && 
            !trimmedValue.toLowerCase().includes('total')) {
          foundMatch = true;
          console.log(`Row ${index}: FOUND MATCH in column ${colIndex}: "${cellValue}"`);
          break;
        }
      }
    }
    
    if (!foundMatch) {
      console.log(`Row ${index}: NO MATCH - checked ${row.length} columns`);
    }
    
    return foundMatch;
  });
  
  console.log(`Filtered result: ${result.length} rows (including headers)`);
  return result;
};

// **CRITICAL FIX 3: Style Preservation with Column Shift**
const preserveStylesWithColumnShift = (
  originalSheet: XLSX.WorkSheet,
  newSheet: XLSX.WorkSheet
): void => {
  // Add column width for new first column
  if (originalSheet['!cols']) {
    newSheet['!cols'] = [{ width: 20 }, ...originalSheet['!cols']];
  }
  
  // Copy row heights
  if (originalSheet['!rows']) {
    newSheet['!rows'] = [...originalSheet['!rows']];
  }
  
  // Shift merged cells by one column
  if (originalSheet['!merges']) {
    newSheet['!merges'] = originalSheet['!merges'].map(merge => ({
      s: { r: merge.s.r, c: merge.s.c + 1 },
      e: { r: merge.e.r, c: merge.e.c + 1 }
    }));
  }
  
  // Copy and shift cell styles
  Object.keys(originalSheet).forEach(cellRef => {
    if (cellRef.startsWith('!')) return;
    
    const { r, c } = XLSX.utils.decode_cell(cellRef);
    const origCell = originalSheet[cellRef];
    
    // Shift cell position by one column
    const newCellRef = XLSX.utils.encode_cell({ r, c: c + 1 });
    
    if (origCell && origCell.s) {
      if (!newSheet[newCellRef]) {
        newSheet[newCellRef] = { t: 's', v: '' };
      }
      newSheet[newCellRef].s = JSON.parse(JSON.stringify(origCell.s));
    }
  });
};

export const ProjectSheetExtractor = () => {
  const [file, setFile] = useState<File | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [projectList, setProjectList] = useState<string[]>([]);
  const [processedData, setProcessedData] = useState<ProcessedData | null>(null);
  const [splitType, setSplitType] = useState<SplitType>("Program");
  const [fileTypeDetected, setFileTypeDetected] = useState<string | null>(null);
  const [processingStatus, setProcessingStatus] = useState<string>("");
  const [currentProgram, setCurrentProgram] = useState<string>("");
  const [activeTab, setActiveTab] = useState<"interim" | "permanent">("interim");
  const [processedItems, setProcessedItems] = useState<ProcessedItem[]>([]);
  const [selectedItem, setSelectedItem] = useState<ProcessedItem | null>(null);
  const [itemHeaders, setItemHeaders] = useState<{[key: string]: any[]}>({});
  const [currentProcessor, setCurrentProcessor] = useState<KPIDataProcessor>(new KPIDataProcessor(IH_CONFIG));
  const [processingStats, setProcessingStats] = useState<any>(null);

  const handleTabChange = (tab: "interim" | "permanent") => {
    setActiveTab(tab);
    const config = tab === "interim" ? IH_CONFIG : PH_CONFIG;
    setCurrentProcessor(new KPIDataProcessor(config));
    // Clear all data when switching tabs
    setProcessedItems([]);
    setSelectedItem(null);
    setItemHeaders({});
    setProcessedWorkOrders([]);
    setSelectedWorkOrder(null);
    setWorkOrderHeaders({});
    setFile(null);
  };
  // Legacy support for old processing
  const [processedWorkOrders, setProcessedWorkOrders] = useState<ProcessedWorkOrder[]>([]);
  const [selectedWorkOrder, setSelectedWorkOrder] = useState<ProcessedWorkOrder | null>(null);
  const [workOrderHeaders, setWorkOrderHeaders] = useState<{[key: string]: any[]}>({});

  const determineSplitType = (filename: string): SplitType => {
    const filenameLower = filename.toLowerCase();

    if (filenameLower.includes("ph") || filenameLower.includes("permanent") || filenameLower.startsWith("icms")) {
      return "Provider";
    }
    if (filenameLower.includes("ih") || filenameLower.includes("interim")) {
      return "Program";
    }

    // Default based on active tab
    return activeTab === "permanent" ? "Provider" : "Program";
  };

  const identifySummarySheet = (workbook: XLSX.WorkBook): string | undefined => {
    const summarySheets = workbook.SheetNames.filter(name => 
      name.toLowerCase().includes("summary") || 
      name.toLowerCase().includes("overview")
    );

    if (summarySheets.length > 0) {
      return summarySheets[0];
    }

    return workbook.SheetNames[0];
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files && files[0]) {
      const uploadedFile = files[0];
      const detectedType = determineSplitType(uploadedFile.name);

      setSplitType(detectedType);

      if (detectedType === "Provider") {
        setFileTypeDetected("PH KPI Report detected - splitting by Provider");
      } else if (detectedType === "ParentProvider") {
        setFileTypeDetected("Parent Provider Report detected - splitting by Parent Provider");
      } else {
        setFileTypeDetected("IH KPI Report detected - splitting by Work Order");
      }

      setFile(uploadedFile);
      parseFile(uploadedFile, detectedType);
    }
  };

  const analyzeSheetStructure = (workbook: XLSX.WorkBook) => {
    console.log("Analyzing workbook structure:");
    console.log(`Workbook has ${workbook.SheetNames.length} sheets:`, workbook.SheetNames);

    workbook.SheetNames.forEach(sheetName => {
      const worksheet = workbook.Sheets[sheetName];
      const jsonData = XLSX.utils.sheet_to_json(worksheet);

      console.log(`Sheet "${sheetName}" has ${jsonData.length} rows`);

      if (jsonData.length > 0) {
        const firstRow = jsonData[0] as Record<string, unknown>;
        console.log(`Columns in "${sheetName}":`, Object.keys(firstRow));
        
        if (sheetName.toLowerCase().includes("summary")) {
          const rowsData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
          if (rowsData.length >= 3) {
            console.log(`Summary Sheet - Row 1:`, rowsData[0]);
            console.log(`Summary Sheet - Row 2:`, rowsData[1]);
            console.log(`Summary Sheet - Row 3:`, rowsData[2]);
          }
        }
      }
    });
  };

  const extractWorkOrdersFromIH = (workbook: XLSX.WorkBook) => {
    console.log("Extracting work orders from IH file with specific column mappings");
    
    const workOrderSet = new Set<string>();
    const headers: {[key: string]: any[]} = {};
    
    // Column mappings as specified
    const columnMappings = {
      'summary': 14, // Column O (0-indexed = 14)
      'kpi1': 16,    // Column Q (0-indexed = 16)
      'kpi2': 8,     // Column I (0-indexed = 8)
      'kpi3': 8      // Column I (0-indexed = 8)
    };
    
    // Process only the required sheets
    const requiredSheets = ['summary', 'kpi1', 'kpi2', 'kpi3'];
    
    requiredSheets.forEach(sheetName => {
      const actualSheetName = workbook.SheetNames.find(name => 
        name.toLowerCase() === sheetName || 
        name.toLowerCase().includes(sheetName)
      );
      
      if (!actualSheetName) {
        console.warn(`Required sheet '${sheetName}' not found`);
        return;
      }
      
      const worksheet = workbook.Sheets[actualSheetName];
      const data = XLSX.utils.sheet_to_json(worksheet, { header: 1 }) as any[][];
      
      // Save the header (row 3 - index 2)
      if (data.length > 2) {
        headers[sheetName] = data[2];
        console.log(`Saved header for ${sheetName}:`, data[2]);
      }
      
      // Get the column index for this sheet
      const columnIndex = columnMappings[sheetName as keyof typeof columnMappings];
      console.log(`Extracting from ${sheetName}, column index ${columnIndex}`);
      
      // Extract work orders from the specified column (start from row 4 since row 3 is header)
      for (let i = 3; i < data.length; i++) {
        const row = data[i];
        if (row && row[columnIndex]) {
          const workOrder = String(row[columnIndex]).trim();
          if (workOrder && 
              workOrder !== '' && 
              workOrder.includes('ODR-IHS') && 
              !workOrder.toLowerCase().includes('total')) {
            workOrderSet.add(workOrder);
          }
        }
      }
    });
    
    const workOrders = Array.from(workOrderSet).sort();
    console.log(`Found ${workOrders.length} unique work orders`);
    
    return { workOrders, headers };
  };
  
  const processWorkOrderData = (workbook: XLSX.WorkBook, workOrders: string[], headers: {[key: string]: any[]}) => {
    console.log("Processing work order data");
    
    const processedOrders: ProcessedWorkOrder[] = [];
    
    // Column mappings
    const columnMappings = {
      'summary': 14,
      'kpi1': 16,
      'kpi2': 8,
      'kpi3': 8
    };
    
    workOrders.forEach(workOrder => {
      const workOrderData: WorkOrderData = {
        workOrder,
        summary: [],
        kpi1: [],
        kpi2: [],
        kpi3: []
      };
      
      // Process each sheet
      ['summary', 'kpi1', 'kpi2', 'kpi3'].forEach(sheetName => {
        const actualSheetName = workbook.SheetNames.find(name => 
          name.toLowerCase() === sheetName || 
          name.toLowerCase().includes(sheetName)
        );
        
        if (!actualSheetName) return;
        
        const worksheet = workbook.Sheets[actualSheetName];
        const data = XLSX.utils.sheet_to_json(worksheet, { header: 1 }) as any[][];
        const columnIndex = columnMappings[sheetName as keyof typeof columnMappings];
        
        // Add header first (remove original Work Order column to avoid duplication)
        if (headers[sheetName]) {
          const headerWithoutWorkOrder = [...headers[sheetName]];
          headerWithoutWorkOrder.splice(columnIndex, 1); // Remove original Work Order column
          (workOrderData[sheetName as keyof WorkOrderData] as any[][]).push(["ClientID", ...headerWithoutWorkOrder]);
        }
        
        // Filter and add data rows (start from row 4 since row 3 is header)
        for (let i = 3; i < data.length; i++) {
          const row = data[i];
          if (row && row[columnIndex] && String(row[columnIndex]).trim() === workOrder) {
            // Add work order as first column, remove original work order column
            const rowWithoutWorkOrder = [...row];
            rowWithoutWorkOrder.splice(columnIndex, 1); // Remove original Work Order column
            (workOrderData[sheetName as keyof WorkOrderData] as any[][]).push([workOrder, ...rowWithoutWorkOrder]);
          }
        }
      });
      
      processedOrders.push({
        workOrder,
        displayName: workOrder.substring(0, 3) + "...",
        fullName: workOrder,
        data: workOrderData
      });
    });
    
    return processedOrders;
  };

  const parseFile = (uploadedFile: File, type: SplitType) => {
    setIsProcessing(true);
    setProgress(10);

    // Auto switch tabs based on detected data type
    if (type === "Program" && activeTab !== "interim") {
      console.log("Auto-switching to IH tab for Program data");
      setActiveTab("interim");
    }
    if ((type === "Provider" || type === "ParentProvider") && activeTab !== "permanent") {
      console.log("Auto-switching to PH tab for Provider data");
      setActiveTab("permanent");
    }

    const reader = new FileReader();

    reader.onload = (e: ProgressEvent<FileReader>) => {
      try {
        if (!e.target?.result) {
          throw new Error("Failed to read file");
        }
        
        const data = new Uint8Array(e.target.result as ArrayBuffer);
        const workbook = XLSX.read(data, {
          type: 'array',
          cellDates: true,
          cellStyles: true,
          cellNF: true,
          cellFormula: true,
          sheetStubs: true
        });
        
        // Use the new abstract processor for both IH and PH
        console.log(`Processing file - activeTab: ${activeTab}, type: ${type}`);
        if (type === "Program" || type === "Provider" || type === "ParentProvider") {
          console.log("Using new processor for type:", type);
          
          // Select the correct processor based on data type
          const processor = type === "Program" ? 
            new KPIDataProcessor(IH_CONFIG) : 
            new KPIDataProcessor(PH_CONFIG);
            
          const result = processor.process(workbook);
          console.log("Processor result:", result);
          console.log("Setting processedItems with", result.items.length, "items");
          console.log("Processing Stats:", result.stats);
          setProcessedItems(result.items);
          setItemHeaders(result.headers);
          setProcessingStats(result.stats);
          setSelectedItem(null);
          
          // Legacy support - also set the old state for IH
          if (type === "Program") {
            const legacyWorkOrders = result.items.map(item => ({
              workOrder: item.key,
              displayName: item.displayName,
              fullName: item.fullName,
              data: {
                workOrder: item.key,
                summary: item.data.summary || [],
                kpi1: item.data.kpi1 || [],
                kpi2: item.data.kpi2 || [],
                kpi3: item.data.kpi3 || []
              }
            }));
            setProcessedWorkOrders(legacyWorkOrders);
            setWorkOrderHeaders(result.headers);
            setProjectList(result.items.map(item => item.key));
          }
          
          setProcessedData({ workbook, allSheetData: {}, summarySheet: 'summary' });
          setProgress(100);
          const itemType = type === "Program" ? "work orders" : "providers";
          toast.success(`Found ${result.items.length} ${itemType}`);
          setIsProcessing(false);
          return;
        }
        
        analyzeSheetStructure(workbook);
        setProgress(30);
        
        let summarySheet: string | undefined;
        if (type === "Program") {
          summarySheet = identifySummarySheet(workbook);
          console.log(`Identified summary sheet: ${summarySheet}`);
        }
        
        setProgress(40);
        
        const values = new Set<string>();
        const allSheetData: SheetData = {};
        const programsList: string[] = [];

        if (type === "Provider" || type === "ParentProvider") {
          const summarySheetName = workbook.SheetNames.find(name => 
            name.toLowerCase() === "summary"
          );
          
          console.log(`Looking for summary sheet. Found: ${summarySheetName}`);
          console.log(`All sheet names:`, workbook.SheetNames);
          
          if (summarySheetName) {
            const summarySheet = workbook.Sheets[summarySheetName];
            const summaryData = XLSX.utils.sheet_to_json(summarySheet, { header: 1 }) as any[][];
            
            for (let i = 3; i < summaryData.length; i++) {
              const row = summaryData[i];
              if (!row || !row[0]) continue;
              
              const cellValue = row[0].toString().trim();
              
              if (cellValue && 
                  cellValue !== "" && 
                  !cellValue.toLowerCase().includes("grand total") &&
                  !cellValue.toLowerCase().includes("total")) {
                
                if (type === "ParentProvider") {
                  programsList.push(cellValue);
                  const parentOrg = getParentOrganization(cellValue);
                  console.log(`Program: "${cellValue}" -> Parent: "${parentOrg}"`);
                  values.add(parentOrg);
                } else {
                  values.add(cellValue);
                }
              }
            }
          }
          
          if (type === "ParentProvider") {
            setAllPrograms(programsList);
          }
        }

        workbook.SheetNames.forEach(sheetName => {
          const worksheet = workbook.Sheets[sheetName];
          
          let jsonData;
          if (type === "Program") {
            jsonData = XLSX.utils.sheet_to_json(worksheet);
          } else {
            jsonData = XLSX.utils.sheet_to_json(worksheet);
          }
          
          allSheetData[sheetName] = jsonData;
          
          if (jsonData.length > 0) {
            console.log(`First row in sheet ${sheetName}:`, jsonData[0]);
          }
          
          if (type === "Program") {
            jsonData.forEach((row: any) => {
              const rowObj = row as Record<string, unknown>;
              
              const programValue = 
                row.project || 
                row.Project || 
                row["project"] ||
                row["Project"] ||
                row.PROJECT ||
                row.program ||
                row.Program ||
                row.PROGRAM ||
                row["Program Name"] ||
                row["program name"] ||
                row.ProgramName ||
                row.programName ||
                Object.entries(rowObj).find(([key, value]) => 
                  key.toLowerCase().includes("project") || 
                  key.toLowerCase().includes("program") ||
                  (typeof value === "string" && 
                   (value.includes("MIST") || value.includes("Project")))
                )?.[1];
                
              if (programValue) {
                values.add(programValue.toString());
              }
            });
          }
        });
        
        setProgress(70);
        
        const uniqueValues = Array.from(values) as string[];
        setProjectList(uniqueValues);
        setProcessedData({ workbook, allSheetData, summarySheet });
        setProgress(100);
        
        toast.success(`File processed. Found ${uniqueValues.length} unique ${(type as string).toLowerCase()} values.`);
      } catch (error) {
        console.error("Error parsing Excel file:", error);
        toast.error("Failed to process the Excel file. Please check the format.");
      } finally {
        setIsProcessing(false);
      }
    };

    reader.onerror = () => {
      toast.error("Error reading file");
      setIsProcessing(false);
    };

    reader.readAsArrayBuffer(uploadedFile);
  };

  // **CRITICAL FIX 4: Complete generateZipFile Rewrite**
  const generateWorkOrderZip = async () => {
    // Handle both IH (work orders) and PH (providers)
    if (activeTab === "interim" && processedWorkOrders.length === 0) {
      toast.error("No work orders to export");
      return;
    }
    if (activeTab === "permanent" && processedItems.length === 0) {
      toast.error("No providers to export");
      return;
    }
    
    setIsProcessing(true);
    setProgress(0);
    
    const itemType = activeTab === "interim" ? "work order" : "provider";
    const items = activeTab === "interim" ? processedWorkOrders : processedItems;
    const sheets = activeTab === "interim" ? ['summary', 'kpi1', 'kpi2', 'kpi3'] : ['summary', 'kpi1-6mo', 'kpi1-1yr', 'kpi2', 'kpi3'];
    
    setProcessingStatus(`Creating ZIP file with ${itemType} files...`);
    
    try {
      const zip = new JSZip();
      
      for (let i = 0; i < items.length; i++) {
        const item = items[i];
        setProgress(Math.floor((i / items.length) * 90));
        setProcessingStatus(`Processing ${itemType} ${i + 1} of ${items.length}`);
        
        const newWorkbook = XLSX.utils.book_new();
        
        // Add each sheet with data
        sheets.forEach(sheetName => {
          let sheetData: any[][];
          if (activeTab === "interim") {
            sheetData = (item as any).data[sheetName as keyof WorkOrderData];
          } else {
            sheetData = (item as ProcessedItem).data[sheetName];
          }
          
          if (sheetData && Array.isArray(sheetData) && sheetData.length > 0) {
            // Use EXACT same logic as UI report display
            const headers = sheetData[0] || [];
            const dataRows = sheetData.slice(1);
            
            // Format data exactly like UI display
            const formattedData = [
              headers, // Keep headers as-is
              ...dataRows.map(row => 
                row.map((cell: any, cellIndex: number) => 
                  formatCellValue(cell, headers[cellIndex] || '', sheetName)
                )
              )
            ];
            
            // Create Excel sheet using the same formatted data as UI display
            const worksheet = XLSX.utils.aoa_to_sheet(formattedData);
            XLSX.utils.book_append_sheet(newWorkbook, worksheet, sheetName.toUpperCase());
          }
        });
        
        // Generate Excel file
        const excelBuffer = XLSX.write(newWorkbook, {
          bookType: 'xlsx',
          type: 'array',
          cellDates: true,
          cellStyles: true
        });
        
        // Add to zip with safe filename
        const itemKey = activeTab === "interim" ? (item as any).workOrder : (item as ProcessedItem).key;
        const safeFileName = itemKey.replace(/[/\\?%*:|"<>]/g, '-');
        const prefix = activeTab === "interim" ? "WorkOrder" : "Provider";
        zip.file(`${prefix}_${safeFileName}.xlsx`, excelBuffer);
      }
      
      setProgress(95);
      setProcessingStatus("Generating ZIP file...");
      
      const zipContent = await zip.generateAsync({ type: 'blob' });
      const zipFileName = activeTab === "interim" ? 'IH_KPI_WorkOrders.zip' : 'PH_KPI_Providers.zip';
      saveAs(zipContent, zipFileName);
      
      setProgress(100);
      toast.success(`Generated ZIP with ${items.length} ${itemType} files`);
      
      setTimeout(() => {
        setIsProcessing(false);
        setProcessingStatus("");
      }, 2000);
      
    } catch (error) {
      console.error("Error generating ZIP:", error);
      toast.error("Failed to generate ZIP file");
      setIsProcessing(false);
      setProcessingStatus("");
    }
  };
  
  const generateZipFile = async () => {
    // Use the new function for IH files
    if (activeTab === "interim" && processedWorkOrders.length > 0) {
      return generateWorkOrderZip();
    }
    
    console.log("🚀 generateZipFile called!");
    if (!processedData) {
      toast.error("Please upload and process a file first");
      return;
    }

    console.log("✅ Setting processing state...");
    setIsProcessing(true);
    setProgress(10);
    setProcessingStatus("Initializing file generation...");
    console.log("📊 Processing state set, popup should be visible");

    try {
      const { workbook } = processedData;
      const zip = new JSZip();

      // Process each unique value
      for (let index = 0; index < projectList.length; index++) {
        const splitValue = projectList[index];
        setCurrentProgram(splitValue);
        setProcessingStatus(`Processing ${index + 1} of ${projectList.length} programs...`);
        
        const newWorkbook = XLSX.utils.book_new();

        // Process each sheet and only include sheets that have data for this program
        let hasAnyData = false;
        
        for (const sheetName of workbook.SheetNames) {
          const originalSheet = workbook.Sheets[sheetName];
          const originalRows = XLSX.utils.sheet_to_json(originalSheet, { header: 1 }) as any[][];
          
          console.log(`\n--- Processing sheet "${sheetName}" for program "${splitValue}" ---`);
          
          // Filter rows for this split value
          const isInSummarySheet = sheetName.toLowerCase().includes("summary");
          const filteredRows = filterRowsForValue(originalRows, splitValue, isInSummarySheet);
          
          // Check if we actually found data (more than just headers)
          const headerRowCount = isInSummarySheet ? 3 : 2;
          const dataRowCount = filteredRows.length - headerRowCount;
          
          console.log(`Sheet "${sheetName}": ${dataRowCount} data rows found`);
          
          // Skip sheets with no actual data rows
          if (dataRowCount <= 0) {
            console.log(`Skipping sheet "${sheetName}" - no data rows`);
            continue;
          }
          
          // Insert split column as first column
          const rowsWithSplitColumn = insertSplitColumn(filteredRows, splitValue, splitType);
          
          // Apply formatting to the data
          const formattedRows = rowsWithSplitColumn.map((row, rowIndex) => {
            if (rowIndex === 0) return row; // Keep header as-is
            return row.map((cellValue: any, colIndex: number) => {
              if (colIndex === 0) return cellValue; // Keep split column as-is
              const headerRow = rowsWithSplitColumn[0];
              const columnHeader = headerRow && headerRow[colIndex] ? headerRow[colIndex] : '';
              return formatCellValue(cellValue, columnHeader, sheetName);
            });
          });
          
          // Create new sheet
          const newSheet = XLSX.utils.aoa_to_sheet(formattedRows);
          
          // Preserve styles with column shift
          preserveStylesWithColumnShift(originalSheet, newSheet);
          
          // Add to workbook
          XLSX.utils.book_append_sheet(newWorkbook, newSheet, sheetName);
          hasAnyData = true;
          
          console.log(`Added sheet "${sheetName}" with ${dataRowCount} data rows`);
        }
        
        // Skip this entire workbook if no sheets had data
        if (!hasAnyData) {
          console.log(`\n❌ Skipping workbook for "${splitValue}" - no sheets contained data`);
          continue;
        }
        
        // Generate Excel file
        const excelBuffer = XLSX.write(newWorkbook, {
          bookType: 'xlsx',
          type: 'array',
          cellStyles: true,
          cellDates: true
        });
        
        // Add to zip
        const safeFileName = splitValue.replace(/[/\\?%*:|"<>]/g, '-');
        zip.file(`${safeFileName}.xlsx`, excelBuffer);
        
        setProgress(10 + Math.floor((index + 1) / projectList.length * 80));
      }
      
      // Generate and save zip
      setProcessingStatus("Generating ZIP file...");
      const zipContent = await zip.generateAsync({ type: 'blob' });
      const columnType = splitType === "Program" ? "Work_Order" : "Provider";
      saveAs(zipContent, `KPI_Reports_by_${columnType}.zip`);
      
      setProgress(100);
      setProcessingStatus("✅ Complete! Files are downloading...");
      
      // Auto-close after 3 seconds to show completion
      setTimeout(() => {
        setIsProcessing(false);
        setProcessingStatus("");
        setCurrentProgram("");
      }, 3000);
      
      toast.success(`Generated ${projectList.length} files with ${columnType} as first column!`);
      
    } catch (error) {
      console.error("Error generating zip file:", error);
      const errorMessage = error instanceof Error ? error.message : "Unknown error occurred";
      toast.error(`Failed to generate files: ${errorMessage}`);
    } finally {
      // Only clear on error - success case uses setTimeout for auto-close
      if (!processingStatus.includes("Complete")) {
        setIsProcessing(false);
        setProcessingStatus("");
        setCurrentProgram("");
      }
    }
  };

  return (
    <div className="container mx-auto py-10 max-w-full px-4">
      <Card className="w-full">
        <CardHeader>
          <CardTitle>KPI Report Processor</CardTitle>
          <CardDescription>
            Process and split KPI Excel reports by Work Order (Interim Housing) or Provider (Permanent Housing)
          </CardDescription>
        </CardHeader>
        
        <CardContent>
          <Tabs value={activeTab} onValueChange={(v) => handleTabChange(v as "interim" | "permanent")}>
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="interim">Interim Housing</TabsTrigger>
              <TabsTrigger value="permanent">
                Permanent Housing
              </TabsTrigger>
            </TabsList>
            
            <TabsContent value="interim" className="space-y-6">
            {fileTypeDetected && (
              <Alert>
                <FileType className="h-4 w-4 mr-2" />
                <AlertDescription>
                  {fileTypeDetected}
                </AlertDescription>
              </Alert>
            )}
            
            <div className="flex items-center justify-center w-full">
              <label 
                htmlFor="dropzone-file" 
                className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed rounded-lg cursor-pointer bg-gray-50 hover:bg-gray-100 border-gray-300"
              >
                <div className="flex flex-col items-center justify-center pt-5 pb-6">
                  <Upload className="w-8 h-8 mb-3 text-gray-500" />
                  <p className="mb-2 text-sm text-gray-500">
                    <span className="font-semibold">Click to upload</span> or drag and drop
                  </p>
                  <p className="text-xs text-gray-500">{activeTab === "interim" ? "IH" : "PH"} KPI Excel spreadsheet (XLSX)</p>
                </div>
                <input 
                  id="dropzone-file" 
                  type="file" 
                  className="hidden" 
                  accept=".xlsx,.xls" 
                  onChange={handleFileUpload}
                  disabled={isProcessing}
                />
              </label>
            </div>
            
            {isProcessing && (
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span>Processing...</span>
                  <span>{progress}%</span>
                </div>
                <Progress value={progress} className="h-2" />
              </div>
            )}
            
            
            {processedWorkOrders.length > 0 && activeTab === "interim" && (
              <div className="mt-6">
                <div className="grid gap-6" style={{gridTemplateColumns: "250px minmax(0, 1fr)"}}>
                  {/* Work Order List */}
                  <div>
                    <h3 className="text-lg font-medium mb-2">
                      Work Orders ({processedWorkOrders.length})
                    </h3>
                    <div className="h-[600px] border rounded-md bg-gray-50 overflow-y-auto flex-shrink-0">
                      <div className="p-2">
                        {processedWorkOrders.map((wo, index) => (
                          <Button
                            key={index}
                            variant={selectedWorkOrder?.workOrder === wo.workOrder ? "secondary" : "ghost"}
                            className="w-full justify-between mb-1"
                            onClick={() => setSelectedWorkOrder(wo)}
                          >
                            <span className="font-mono text-sm">{wo.fullName}</span>
                            <span className="text-xs text-muted-foreground">
                              {wo.data.summary.length - 1} rows
                            </span>
                          </Button>
                        ))}
                      </div>
                    </div>
                  </div>
                  
                  {/* Work Order Details */}
                  <div>
                    <h3 className="text-lg font-medium mb-2">Details</h3>
                    {/* Header Section */}
                    <div className="mb-4 p-4 bg-slate-50 border rounded-md">
                      <div className="text-center mb-3">
                        <h2 className="text-lg font-bold text-slate-800">Interim Housing Q1 FY 25-26: July-Sept 2025</h2>
                      </div>
                      <div className="grid grid-cols-3 gap-4 text-sm text-slate-700 mb-3">
                        <div className="text-center">
                          <strong>KPI 1:</strong> Positive Exits from Interim Housing<br/>
                          <span className="text-slate-600">Target: 65%</span>
                        </div>
                        <div className="text-center">
                          <strong>KPI 2:</strong> Linked to MBA<br/>
                          <span className="text-slate-600">Target: 95%</span>
                        </div>
                        <div className="text-center">
                          <strong>KPI 3:</strong> Linked to PCP<br/>
                          <span className="text-slate-600">Target: 90%</span>
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex justify-between items-center mb-4">
                      <h3 className="text-lg font-medium">
                        {selectedWorkOrder ? `Work Order: ${selectedWorkOrder.fullName}` : "Select a Work Order"}
                      </h3>
                      {processedWorkOrders.length > 0 && (
                        <Button
                          onClick={generateWorkOrderZip}
                          disabled={isProcessing}
                          className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white"
                        >
                          <Download className="w-4 h-4" />
                          Download All Work Orders (ZIP)
                        </Button>
                      )}
                    </div>
                    {selectedWorkOrder ? (
                      <div className="w-full border rounded-md bg-white overflow-auto">
                        <Tabs defaultValue="summary" className="w-full">
                          <div className="p-4 pb-0 sticky top-0 bg-white z-10">
                            <TabsList className="grid w-full grid-cols-4">
                              <TabsTrigger value="summary">Summary</TabsTrigger>
                              <TabsTrigger value="kpi1">KPI1</TabsTrigger>
                              <TabsTrigger value="kpi2">KPI2</TabsTrigger>
                              <TabsTrigger value="kpi3">KPI3</TabsTrigger>
                            </TabsList>
                          </div>
                          {['summary', 'kpi1', 'kpi2', 'kpi3'].map(sheet => (
                            <TabsContent key={sheet} value={sheet}>
                              <div className="text-sm space-y-4 p-4 pt-0">
                                <div className="flex justify-between items-center">
                                  <p className="font-medium">Rows: {selectedWorkOrder.data[sheet as keyof WorkOrderData].length - 1}</p>
                                  <p className="text-muted-foreground">Columns: {(selectedWorkOrder.data[sheet as keyof WorkOrderData] as any[][])[0]?.length || 0}</p>
                                </div>
                                {selectedWorkOrder.data[sheet as keyof WorkOrderData].length > 0 && (
                                  <div>
                                    <Table>
                                      <TableHeader>
                                        <TableRow>
                                          {(selectedWorkOrder.data[sheet as keyof WorkOrderData] as any[][])[0]?.map((header: any, i: number) => (
                                            <TableHead key={i} className="text-xs whitespace-nowrap">
                                              {String(header || `Col ${i}`)}
                                            </TableHead>
                                          ))}
                                        </TableRow>
                                      </TableHeader>
                                      <TableBody>
                                        {(selectedWorkOrder.data[sheet as keyof WorkOrderData] as any[][]).slice(1).map((row: any[], rowIndex: number) => (
                                          <TableRow key={rowIndex}>
                                            {row.map((cell: any, cellIndex: number) => (
                                              <TableCell key={cellIndex} className="text-xs whitespace-nowrap">
                                                {formatCellValue(cell, (selectedWorkOrder.data[sheet as keyof WorkOrderData] as any[][])[0]?.[cellIndex] || '', sheet)}
                                              </TableCell>
                                            ))}
                                          </TableRow>
                                        ))}
                                      </TableBody>
                                    </Table>
                                  </div>
                                )}
                              </div>
                            </TabsContent>
                          ))}
                        </Tabs>
                      </div>
                    ) : (
                      <div className="h-[600px] border rounded-md bg-gray-50 flex items-center justify-center">
                        <div className="text-center text-muted-foreground">
                          <FileSpreadsheet className="w-12 h-12 mx-auto mb-2 opacity-50" />
                          <p>Click on a work order to view details</p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
            </TabsContent>
            
            <TabsContent value="permanent" className="space-y-6">
              {fileTypeDetected && (
                <Alert>
                  <FileType className="h-4 w-4 mr-2" />
                  <AlertDescription>
                    {fileTypeDetected}
                  </AlertDescription>
                </Alert>
              )}
              
              <div className="flex items-center justify-center w-full">
                <label 
                  htmlFor="dropzone-file" 
                  className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed rounded-lg cursor-pointer bg-gray-50 hover:bg-gray-100 border-gray-300"
                >
                  <div className="flex flex-col items-center justify-center pt-5 pb-6">
                    <Upload className="w-8 h-8 mb-3 text-gray-500" />
                    <p className="mb-2 text-sm text-gray-500">
                      <span className="font-semibold">Click to upload</span> or drag and drop
                    </p>
                    <p className="text-xs text-gray-500">PH KPI Excel spreadsheet (XLSX)</p>
                  </div>
                  <input 
                    id="dropzone-file" 
                    type="file" 
                    className="hidden" 
                    accept=".xlsx,.xls" 
                    onChange={handleFileUpload}
                    disabled={isProcessing}
                  />
                </label>
              </div>
              
              {isProcessing && (
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span>Processing...</span>
                    <span>{progress}%</span>
                  </div>
                  <Progress value={progress} className="h-2" />
                </div>
              )}
              
              {false && processingStats && activeTab === "permanent" && (
                <div className="mt-4 p-4 bg-slate-100 rounded-md">
                  <h4 className="font-medium mb-2">Processing Statistics</h4>
                  <div className="grid grid-cols-3 gap-4 text-sm">
                    <div>
                      <span className="font-semibold">Total Rows Read:</span> {processingStats.totalRowsRead}
                    </div>
                    <div>
                      <span className="font-semibold">Rows Processed:</span> {processingStats.totalRowsProcessed}
                    </div>
                    <div>
                      <span className="font-semibold">Filtered Rows:</span> {processingStats.orphanedRows}
                    </div>
                  </div>
                  <div className="mt-2">
                    <span className="font-semibold">Sheet Details:</span>
                    <div className="ml-4 text-xs">
                      {Object.entries(processingStats.sheetStats || {}).map(([sheetName, stats]: [string, any]) => (
                        <div key={sheetName} className="flex justify-between">
                          <span>{sheetName}:</span>
                          <span>{stats.totalRows} total, {stats.processedRows} processed, {stats.orphanedRows} filtered</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
              
              {processedItems.length > 0 && activeTab === "permanent" && (
                <div className="mt-6">
                  <div className="grid gap-6" style={{gridTemplateColumns: "400px minmax(0, 1fr)"}}>
                    {/* Provider List */}
                    <div>
                      <h3 className="text-lg font-medium mb-2">
                        Providers ({processedItems.length})
                      </h3>
                      <div className="h-[600px] border rounded-md bg-gray-50 overflow-y-auto flex-shrink-0">
                        <div className="p-2">
                          {processedItems.map((item, index) => (
                            <Button
                              key={index}
                              variant={selectedItem?.key === item.key ? "secondary" : "ghost"}
                              className="w-full justify-start mb-1"
                              onClick={() => setSelectedItem(item)}
                            >
                              <div className="flex w-full justify-between">
                                <span className="font-mono text-xs text-left">{item.fullName}</span>
                                <span className="text-xs text-muted-foreground text-right">
                                  {item.data.summary?.length - 1 || 0} rows
                                </span>
                              </div>
                            </Button>
                          ))}
                        </div>
                      </div>
                    </div>
                    
                    {/* Provider Details */}
                    <div>
                      <h3 className="text-lg font-medium mb-2">Details</h3>
                      {/* Header Section */}
                      <div className="mb-4 p-4 bg-slate-50 border rounded-md">
                        <div className="text-center mb-3">
                          <h2 className="text-lg font-bold text-slate-800">Permanent Housing Q1 FY 25-26: July-Sept 2025</h2>
                        </div>
                        <div className="grid grid-cols-3 gap-4 text-sm text-slate-700 mb-3">
                          <div className="text-center">
                            <strong>KPI 1:</strong> Housing Retention<br/>
                            <span className="text-slate-600">Target: 90%</span>
                          </div>
                          <div className="text-center">
                            <strong>KPI 2:</strong> Linked to MBA<br/>
                            <span className="text-slate-600">Target: 95%</span>
                          </div>
                          <div className="text-center">
                            <strong>KPI 3:</strong> Linked to PCP<br/>
                            <span className="text-slate-600">Target: 90%</span>
                          </div>
                        </div>
                      </div>
                      
                      <div className="flex justify-between items-center mb-4">
                        <h3 className="text-lg font-medium">
                          {selectedItem ? `Provider: ${selectedItem.fullName}` : "Select a Provider"}
                        </h3>
                        {processedItems.length > 0 && (
                          <Button
                            onClick={generateWorkOrderZip}
                            disabled={isProcessing}
                            className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white"
                          >
                            <Download className="w-4 h-4" />
                            Download All Providers (ZIP)
                          </Button>
                        )}
                      </div>
                      {selectedItem ? (
                        <div className="w-full border rounded-md bg-white overflow-auto">
                          <Tabs defaultValue="summary" className="w-full">
                            <div className="p-4 pb-0 sticky top-0 bg-white z-10">
                              <TabsList className="grid w-full grid-cols-5">
                                <TabsTrigger value="summary">Summary</TabsTrigger>
                                <TabsTrigger value="kpi1-6mo">KPI1-6Mo</TabsTrigger>
                                <TabsTrigger value="kpi1-1yr">KPI1-1Yr</TabsTrigger>
                                <TabsTrigger value="kpi2">KPI2</TabsTrigger>
                                <TabsTrigger value="kpi3">KPI3</TabsTrigger>
                              </TabsList>
                            </div>
                            {['summary', 'kpi1-6mo', 'kpi1-1yr', 'kpi2', 'kpi3'].map(sheet => (
                              <TabsContent key={sheet} value={sheet}>
                                <div className="text-sm space-y-4 p-4 pt-0">
                                  <div className="flex justify-between items-center">
                                    <p className="font-medium">Rows: {selectedItem.data[sheet]?.length - 1 || 0}</p>
                                    <p className="text-muted-foreground">Columns: {selectedItem.data[sheet]?.[0]?.length || 0}</p>
                                  </div>
                                  {selectedItem.data[sheet]?.length > 0 && (
                                    <div>
                                      <Table>
                                        <TableHeader>
                                          <TableRow>
                                            {selectedItem.data[sheet][0]?.map((header: any, i: number) => (
                                              <TableHead key={i} className="text-xs whitespace-nowrap">
                                                {String(header || `Col ${i}`)}
                                              </TableHead>
                                            ))}
                                          </TableRow>
                                        </TableHeader>
                                        <TableBody>
                                          {selectedItem.data[sheet].slice(1).map((row: any[], rowIndex: number) => (
                                            <TableRow key={rowIndex}>
                                              {row.map((cell: any, cellIndex: number) => (
                                                <TableCell key={cellIndex} className="text-xs whitespace-nowrap">
                                                  {formatCellValue(cell, selectedItem.data[sheet][0]?.[cellIndex] || '', sheet)}
                                                </TableCell>
                                              ))}
                                            </TableRow>
                                          ))}
                                        </TableBody>
                                      </Table>
                                    </div>
                                  )}
                                </div>
                              </TabsContent>
                            ))}
                          </Tabs>
                        </div>
                      ) : (
                        <div className="h-[600px] border rounded-md bg-gray-50 flex items-center justify-center">
                          <div className="text-center text-muted-foreground">
                            <FileSpreadsheet className="w-12 h-12 mx-auto mb-2 opacity-50" />
                            <p>Click on a provider to view details</p>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </TabsContent>
          </Tabs>
        </CardContent>
        
        <CardFooter className="flex justify-end">
          {activeTab === "interim" && processedWorkOrders.length === 0 && processedData && false && (
            <Button
              onClick={generateZipFile}
              disabled={!processedData || isProcessing || projectList.length === 0}
              className="flex items-center gap-2"
            >
              <Download className="w-4 h-4" />
              Generate {fileTypes[splitType]} Files
            </Button>
          )}
        </CardFooter>
      </Card>

      {/* Processing Dialog */}
      <Dialog open={isProcessing} onOpenChange={() => {}}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin" />
              Processing KPI Reports
            </DialogTitle>
            <DialogDescription>
              Please wait while we process your KPI files...
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span>Progress</span>
                <span>{progress}%</span>
              </div>
              <Progress value={progress} className="h-2" />
            </div>
            
            {processingStatus && (
              <div className="text-sm text-muted-foreground">
                {processingStatus}
              </div>
            )}
            
            {currentProgram && (
              <div className="text-sm">
                <span className="font-medium">Current Program:</span>
                <div className="text-muted-foreground mt-1 p-2 bg-muted rounded-sm font-mono text-xs">
                  {currentProgram}
                </div>
              </div>
            )}
            
            <div className="text-xs text-muted-foreground">
              Processing {projectList.length} programs across multiple sheets...
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ProjectSheetExtractor;